import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import Loading from 'modules/core/components/Loading'
import { PortalWrapper } from 'modules/core/components/PortalWrapper'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { employeeUser } from 'modules/core/store/collections/users'

import type { RouteContext } from 'modules/employees/lib/types'
import { canAccessWelcomeSpace } from 'modules/employees/lib/utils'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import TasksList from 'modules/tasks/components/TasksList'
import { TaskIdentifier } from 'modules/tasks/lib/task'

const isAuthorized = (employee: Employee): boolean => {
  return employeeUser(employee).isCurrent() || employee.policy.can('manageTasks')
}

function IndexComponent() {
  const { employee: currentEmployee } = useCurrentSession()
  const { navigation: navigation } = useNavigation()
  const { cardEmployeeRight, employeeId } = useOutletContext<RouteContext>()
  const navigate = useNavigate()

  const { loading: loadingCollections } = useAsyncData(
    () => Promise.all([employees.fetch({ withCachedFetchParams: true })]),
    []
  )

  const employee = employees.get(Number(employeeId))

  if (!!employee && !isAuthorized(employee)) {
    navigate(navigation.employees.id.fullPath({ id: employee.id }))

    return null
  }

  if (loadingCollections) {
    return <Loading />
  }

  if (!employee) {
    throw new Error('Employee cannot be found')
  }

  const accessId = employee.attributes.access_id

  const hideActions = canAccessWelcomeSpace(currentEmployee)

  return (
    <ResponsivePage.Body variant="full" showHeader={false}>
      <PortalWrapper container={cardEmployeeRight}></PortalWrapper>
      <TasksList
        involveeId={accessId}
        identifier={TaskIdentifier.Single}
        hideActions={hideActions}
        employee={employee}
        isExternalUser={!currentEmployee}
      />
      <Outlet context={{ currentUserId: accessId }} />
    </ResponsivePage.Body>
  )
}

export default IndexComponent
